"use client";

import { useMemo } from "react";

import { VIMEO_REGEX, YOUTUBE_REGEX } from "@unreserved/constants/app";
import { cn } from "@unreserved/utils/cn";

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  title?: string;
  preload?: "none" | "metadata" | "auto";
}

type VideoType = "youtube" | "vimeo" | "direct";

interface VideoInfo {
  type: VideoType;
  embedUrl: string;
  videoId?: string;
}
function getVideoIdFromYoutubeUrl(url: string): string | null {
  const match = url.match(YOUTUBE_REGEX);
  return match?.[1] ?? null;
}

function getVideoIdFromVimeoUrl(url: string): string | null {
  const match = url.match(VIMEO_REGEX);
  return match?.[1] ?? null;
}

function detectVideoType(src: string): VideoInfo {
  // Check YouTube
  const youtubeId = getVideoIdFromYoutubeUrl(src);
  if (youtubeId) {
    return {
      type: "youtube",
      videoId: youtubeId,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
    };
  }

  // Check Vimeo
  const vimeoId = getVideoIdFromVimeoUrl(src);
  if (vimeoId) {
    return {
      type: "vimeo",
      videoId: vimeoId,
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  // Direct video URL
  return {
    type: "direct",
    embedUrl: src,
  };
}

export function VideoPlayer({
  src,
  poster,
  className,
  controls = true,
  autoPlay = false,
  loop = false,
  muted = false,
  playsInline = true,
  title,
  preload = "none",
}: VideoPlayerProps) {
  const videoInfo = useMemo(() => detectVideoType(src), [src]);

  // Build iframe params for YouTube/Vimeo
  const getIframeUrl = () => {
    const params = new URLSearchParams();

    if (videoInfo.type === "youtube") {
      if (autoPlay) params.append("autoplay", "1");
      if (muted) params.append("mute", "1");
      if (loop && videoInfo.videoId) {
        params.append("loop", "1");
        params.append("playlist", videoInfo.videoId);
      }
      if (!controls) params.append("controls", "0");
      params.append("modestbranding", "1");
      params.append("rel", "0");
    } else if (videoInfo.type === "vimeo") {
      if (autoPlay) params.append("autoplay", "1");
      if (muted) params.append("muted", "1");
      if (loop) params.append("loop", "1");
      params.append("title", "0");
      params.append("byline", "0");
      params.append("portrait", "0");
    }

    const queryString = params.toString();
    return queryString ? `${videoInfo.embedUrl}?${queryString}` : videoInfo.embedUrl;
  };

  // Render native video player for direct URLs
  if (videoInfo.type === "direct") {
    return (
      <div className={cn("relative w-full overflow-hidden rounded-lg", className)}>
        <video
          src={src}
          poster={poster}
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          preload={preload}
          className="h-auto w-full rounded-lg"
          aria-label={title}
          style={{
            aspectRatio: "16 / 9",
            objectFit: "cover",
          }}>
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Render iframe for YouTube/Vimeo
  return (
    <div className={cn("relative w-full overflow-hidden rounded-lg", className)}>
      <div
        className="relative w-full"
        style={{
          aspectRatio: "16 / 9",
        }}>
        <iframe
          src={getIframeUrl()}
          title={title || `${videoInfo.type} video player`}
          className="absolute inset-0 h-full w-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  );
}
